/**
 * Read a meta tag value by name
 * @param {string} name - The meta tag name attribute
 * @returns {string|null} The meta tag content value
 */
export const getMetaContent = (name) => {
  if (typeof document === 'undefined') return null;

  const element = document.querySelector(`meta[name="${name}"]`);
  return element?.getAttribute('content') || null;
};

/** Absolute URL on the web app host (web.<base domain>) for a given path. */
export const rootDomainUrl = (path) => {
  const defaultHost = getMetaContent('env:default_host');
  const host = getMetaContent('env:web_host') || (defaultHost ? `web.${defaultHost}` : null);
  return host ? `https://${host}${path}` : path;
};

/** Absolute URL on the marketing apex for a given path. */
export const marketingUrl = (path) => {
  // Server-provided so development can point these at the marketing app's own
  // dev server — the apex has no local DNS entry and the derived https host
  // would leave the machine.
  const origin = getMetaContent('env:marketing_origin');
  if (origin) return `${origin.replace(/\/$/, '')}${path}`;

  const host = getMetaContent('env:marketing_host') || getMetaContent('env:default_host');
  return host ? `https://${host}${path}` : path;
};

/**
 * Absolute URL on the universal-link host (go.<base domain>) for a given path.
 * Links must live on a different host than the current page for iOS to hand
 * them to the installed app; the server redirects everyone else.
 */
export const appLinkUrl = (path) => {
  const host = getMetaContent('env:default_host');
  return host ? `https://go.${host}${path}` : path;
};

export const getMetaTagsSnapshot = () => {
  return Array.from(document.querySelectorAll('meta[name]'))
    .map((element) => ({
      key: element.getAttribute('name'),
      value: element.getAttribute('content') || null,
    }))
    .sort((a, b) => a.key.localeCompare(b.key));
};

/**
 * Set a meta tag value by name
 * @param {string} name - The meta tag name attribute
 * @param {string|null|undefined} content - The meta tag content value
 */
export const setMetaContent = (name, content) => {
  const element = document.querySelector(`meta[name="${name}"]`);
  if (element) element.setAttribute('content', content == null ? '' : String(content));
};

/**
 * Get the current workspace ID from meta tag.
 *
 * The tag is written by `RouteWorkspaceProvider` on every main-shell workspace
 * switch, so this helper is safe to call from imperative code (stores, Stimulus
 * controllers, non-main-shell apps). Inside the main-shell React tree, prefer
 * `useRouteWorkspace().workspaceId` — it's one fewer indirection and binds
 * renders directly to the routed value.
 * @deprecated Inside any `RouteWorkspaceProvider` tree, prefer
 *   `useRouteWorkspace().workspaceId`. Keep this helper for imperative or
 *   out-of-tree consumers only.
 * @returns {string|null} The current workspace ID
 */
export const currentWorkspaceId = () => {
  return getMetaContent('current_workspace:id');
};

/**
 * Get the current user ID from meta tag (signed-in app sessions only)
 * @returns {string|null} The current user hashid
 */
export const currentUserId = () => {
  return getMetaContent('current_user:id');
};

/**
 * Get the current profile ID from meta tag.
 *
 * Kept in sync by `RouteWorkspaceProvider` on main-shell workspace switches.
 * Inside the main-shell React tree, prefer `useRouteWorkspace().profileId`.
 * @deprecated Inside any `RouteWorkspaceProvider` tree, prefer
 *   `useRouteWorkspace().profileId`. Keep this helper for imperative or
 *   out-of-tree consumers only.
 * @returns {string|null} The current profile ID
 */
export const currentProfileId = () => {
  return getMetaContent('current_profile:id');
};

/**
 * Get the current shadow ID from meta tag
 * @returns {string|null} The current shadow ID
 */
export const currentShadowId = () => {
  return getMetaContent('current_shadow:id');
};

/**
 * Get the current shared channel key from meta tag (for shared channel access flow)
 * @returns {string|null} The current shared channel key
 */
export const currentChannelShareKey = () => {
  return getMetaContent('current_channel:share_key');
};

export const readSharedShellBootstrap = () => {
  const element = document.getElementById('shared-shell-bootstrap');
  if (!element?.textContent) return null;

  try {
    return JSON.parse(element.textContent);
  } catch {
    return null;
  }
};

/**
 * Check if a feature flag is enabled for the current profile
 * @param {string} flag - The feature flag name
 * @returns {boolean} True if the feature flag is enabled, false otherwise
 */
export const isFeatureFlagEnabled = (flag) => {
  return getMetaContent(`feature_flag:${flag}`) === 'enabled';
};

export const SubscriptionConfig = {
  _config: null,

  _getConfig() {
    if (this._config) return this._config;

    const content = getMetaContent('subscription:config');
    if (!content) throw new Error('Subscription config not found in meta tags');

    this._config = JSON.parse(content);
    return this._config;
  },

  for(tier, key) {
    const config = this._getConfig();
    const tierConfig = config.tiers?.[tier] ?? config[tier];
    if (!tierConfig) throw new Error(`Unknown subscription tier: ${tier}`);
    return tierConfig[key];
  },

  plan(planKey) {
    const config = this._getConfig();
    if (!config.plans) throw new Error('Plans not available in subscription config');
    const plan = config.plans[planKey];
    if (!plan) throw new Error(`Unknown subscription plan: ${planKey}`);
    return plan;
  },
};
